/**
 * Presentation mode for showing the admin panel to an audience.
 *
 * There is no longer a control that turns this on; the machinery is kept because the guards it
 * feeds still stand between a stray session flag and real customer data:
 *
 * 1. Reads are served from local fixtures, so no request carries real customer names, emails,
 *    phone numbers or addresses onto a projector.
 * 2. Writes are refused centrally in api/client.ts, so nobody in the room can cancel, merge,
 *    ship or re-import anything by clicking around.
 *
 * The flag lives in sessionStorage rather than localStorage: a presenter who closes the tab
 * cannot come back tomorrow, see fixtures, and conclude that real orders have disappeared.
 */
const KEY = 'teneryfa.demoMode';

export function isDemoMode(): boolean {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    // Private-mode Safari throws on sessionStorage access; treat that as "not demoing".
    return false;
  }
}

/** Leaves demo mode. Nothing in the UI enters it, so this is only an escape hatch. */
export function setDemoMode(enabled: boolean): void {
  try {
    if (enabled) sessionStorage.setItem(KEY, '1');
    else sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  // Full reload: components hold already-fetched rows in state, and a toggle has to swap the
  // whole panel at once rather than leave real and fixture data interleaved on screen.
  window.location.reload();
}
